import { useState } from 'react';
import { MapPin, CheckCircle2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import LocationAutocomplete from './LocationAutocomplete';
import MapPicker from './MapPicker';

/**
 * AddressInput - Reusable address input with autocomplete and optional map picker
 *
 * Combines LocationAutocomplete and MapPicker into a single, easy-to-use component.
 * Used for Service Receivers, Partner Agencies, Job Locations, etc.
 *
 * @param {Object} value - { address: string, lat: number|null, lng: number|null }
 * @param {Function} onChange - Callback with { address, lat, lng }
 * @param {string} placeholder - Input placeholder text
 * @param {boolean} disabled - Disable the input
 * @param {boolean} error - Show error state
 * @param {string} errorMessage - Error message to display
 * @param {boolean} showMapByDefault - Show map picker by default
 * @param {boolean} allowMapToggle - Allow user to toggle map visibility
 * @param {string} mapHeight - Height of the map picker
 * @param {boolean} compact - Use compact styling (for inline forms)
 * @param {string} className - Additional CSS classes
 */
export default function AddressInput({
  value = { address: '', lat: null, lng: null },
  onChange,
  placeholder = 'Search for address...',
  disabled = false,
  error = false,
  errorMessage = '',
  showMapByDefault = false,
  allowMapToggle = true,
  mapHeight = '250px',
  compact = false,
  className
}) {
  const [showMap, setShowMap] = useState(showMapByDefault);

  // Handle address text change from autocomplete
  const handleAddressChange = (address) => {
    onChange?.({
      address,
      lat: value?.lat || null,
      lng: value?.lng || null
    });
  };

  // Handle coordinates change from autocomplete selection
  const handleCoordinatesChange = (location) => {
    if (location) {
      onChange?.({
        address: location.address || value?.address || '',
        lat: location.lat,
        lng: location.lng
      });
    } else {
      onChange?.({
        address: '',
        lat: null,
        lng: null
      });
    }
  };

  // Handle map picker changes (clicking on map or dragging marker)
  const handleMapChange = (location) => {
    if (location) {
      onChange?.({
        address: location.address || value?.address || '',
        lat: location.lat,
        lng: location.lng
      });
    } else {
      onChange?.({
        address: '',
        lat: null,
        lng: null
      });
    }
  };

  const hasCoordinates = value?.lat && value?.lng;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Address Autocomplete Input */}
      <div>
        <LocationAutocomplete
          value={value?.address || ''}
          onChange={handleAddressChange}
          onCoordinatesChange={handleCoordinatesChange}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
        />

        {/* Error Message */}
        {error && errorMessage && (
          <p className="mt-1 text-xs text-red-400">{errorMessage}</p>
        )}

        {/* Coordinates indicator */}
        {hasCoordinates && !compact && (
          <p className="mt-1 text-xs text-accent flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Location verified: {value.lat.toFixed(4)}, {value.lng.toFixed(4)}
          </p>
        )}
      </div>

      {/* Map Toggle Button */}
      {allowMapToggle && !disabled && (
        <button
          type="button"
          onClick={() => setShowMap(!showMap)}
          className={cn(
            'text-sm flex items-center gap-1 transition-colors',
            showMap
              ? 'text-text-secondary hover:text-text-primary'
              : 'text-primary-orange hover:text-primary-orange/80'
          )}
        >
          <MapPin className="w-4 h-4" />
          {showMap ? 'Hide Map' : 'Pin Location on Map'}
        </button>
      )}

      {/* Map Picker */}
      {showMap && (
        <div className="mt-2">
          <MapPicker
            value={hasCoordinates ? {
              lat: value.lat,
              lng: value.lng,
              address: value.address
            } : null}
            onChange={handleMapChange}
            height={mapHeight}
            showSearch={false}
            draggableMarker={!disabled}
            showSatelliteToggle={true}
            disabled={disabled}
          />
          {!compact && (
            <p className="mt-2 text-xs text-text-secondary">
              Click on the map or drag the marker to adjust the exact location.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
